import React from "react";
import {
  Box,
  CircularProgress,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

export type SortDir = "asc" | "desc";

export interface DataTableColumn<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
}

interface DataTableCstProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T, index: number) => string | number;
  emptyLabel: string;
  isLoading?: boolean;
  sortField?: string | null;
  sortDir?: SortDir;
  onSortChange?: (field: string) => void;
  plain?: boolean;
  stickyHeader?: boolean;
  containerSx?: SxProps<Theme>;
}

function DataTableCst<T>({
  columns,
  rows,
  getRowId,
  emptyLabel,
  isLoading,
  sortField,
  sortDir,
  onSortChange,
  plain = false,
  stickyHeader = false,
  containerSx,
}: DataTableCstProps<T>) {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 6,
          fontSize: "13px",
          color: theme.palette.text.tertiary,
        }}
      >
        <Typography>{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      sx={[
        {
          paddingBottom: "10px",
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: "8px",
            backgroundColor: theme.palette.action.disabled,
          },
          "&::-webkit-scrollbar-corner": {
            backgroundColor: "transparent",
          },
        },
        plain
          ? {
              "& .MuiTableRow-root, & .MuiTableRow-root.MuiTableRow-hover:hover":
                {
                  backgroundColor: "transparent !important",
                },
              "& .MuiTableCell-root:first-of-type": {
                paddingLeft: "8px",
              },
              "& .MuiTableCell-body:first-of-type:after": {
                left: "8px",
                width: "calc(100% - 8px - 1rem)",
              },
            }
          : {
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            },
        ...(Array.isArray(containerSx) ? containerSx : [containerSx]),
      ]}
    >
      <Table size="small" stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {columns.map((col) => {
              const clickable = !!col.sortable && !!onSortChange;
              const active = sortField === col.key;
              return (
                <TableCell
                  key={col.key}
                  onClick={
                    clickable ? () => onSortChange?.(col.key) : undefined
                  }
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.divider,
                    cursor: clickable ? "pointer" : "default",
                    userSelect: "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {col.label}
                    {col.sortable &&
                      (active ? (
                        sortDir === "asc" ? (
                          <ArrowUp size={13} />
                        ) : (
                          <ArrowDown size={13} />
                        )
                      ) : (
                        <ArrowUpDown size={13} style={{ opacity: 0.5 }} />
                      ))}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={getRowId(row, index)} hover>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                  }}
                >
                  {col.render
                    ? col.render(row)
                    : ((row as any)[col.key] ?? "-")}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default DataTableCst;
